import { ArrowRightOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Card, Button, List, Avatar } from 'antd';
import { useCallback, useState } from 'react';
import {
    GetCompanySpecsForNewModuleDocument,
    type GetCompanySpecsForNewModuleQuery,
    type GetCompanySpecsForNewModuleQueryVariables,
} from '../../../api/queries/getCompanySpecsForNewModule';
import { useListCompaniesForSelectionQuery } from '../../../api/queries/listCompaniesForSelection';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import LoadingElement from '../../../components/LoadingElement';
import CompanyEmptyListResult from '../CompanyListPage/CompanyEmptyListResult';
import { type CompanySpecs } from './getAvailableModules';

export type ModuleCompanySelectionProps = {
    onSelect: (company: CompanySpecs) => void;
};

const ModuleCompanySelection = ({ onSelect }: ModuleCompanySelectionProps) => {
    const apolloClient = useApolloClient();
    const company = useCompany(true);

    // first get companies
    const { loading, data } = useListCompaniesForSelectionQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: { companyId: company?.id, withModuleCreation: true },
        },
    });

    const [loadingCompanySpecs, setLoadingCompanySpecs] = useState(false);
    const onItemSelect = useCallback(
        (companyId: string) => {
            setLoadingCompanySpecs(true);

            return apolloClient
                .query<GetCompanySpecsForNewModuleQuery, GetCompanySpecsForNewModuleQueryVariables>({
                    query: GetCompanySpecsForNewModuleDocument,
                    variables: { companyId },
                    fetchPolicy: 'network-only',
                })
                .finally(() => {
                    setLoadingCompanySpecs(false);
                })
                .then(({ data }) => {
                    onSelect(data.company);
                });
        },
        [apolloClient, onSelect, setLoadingCompanySpecs]
    );

    const companies = data?.list?.items || [];
    const noCompanies = companies.length === 0;

    if ((noCompanies && loading) || loadingCompanySpecs) {
        return <LoadingElement />;
    }

    if (noCompanies) {
        return <CompanyEmptyListResult />;
    }

    return (
        <Card>
            <List<(typeof companies)[number]>
                dataSource={companies}
                renderItem={company => (
                    <List.Item
                        actions={[
                            <Button
                                icon={<ArrowRightOutlined />}
                                onClick={() => onItemSelect(company.id)}
                                type="link"
                            />,
                        ]}
                    >
                        <List.Item.Meta avatar={<Avatar />} title={company.displayName} />
                    </List.Item>
                )}
            />
        </Card>
    );
};

export default ModuleCompanySelection;
